import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { BaseWeapon, BaseProjectile } from './BaseWeapon';
import { GameConfig } from '../config/GameConfig';

export class CatnipBomb extends BaseProjectile {
  public explosionRadius: number = GameConfig.WEAPON_DATA.CATNIP_BOMB.RADIUS;
  private enemies: Phaser.Physics.Arcade.Group;
  private weaponScene!: Phaser.Scene;

  constructor(scene: Phaser.Scene, x: number, y: number, enemies: Phaser.Physics.Arcade.Group, damage: number) {
    super(scene, x, y, '');
    this.enemies = enemies;
    this.scene = scene;
    this.createBombTexture(scene);
    this.setTexture('projectile-bomb');
  }

  public activate(x: number, y: number, damage: number, radius: number, fuseTime: number): void {
    this.setPosition(x, y);
    this.damage = damage;
    this.explosionRadius = radius;

    this.setActive(true);
    this.setVisible(true);

    // Bomb falls and explodes after delay
    this.weaponScene.time.delayedCall(fuseTime, () => {
      this.explode();
    });

    // Pulse animation
    this.weaponScene.tweens.add({
      targets: this,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 400,
      yoyo: true,
      repeat: 0
    });
  }

  private createBombTexture(scene: Phaser.Scene): void {
    if (scene.textures.exists('projectile-bomb')) {
      return;
    }
    const graphics = scene.add.graphics();
    const size = 16;
    const halfSize = size / 2;

    // Catnip leaf bundle
    graphics.fillStyle(0x90EE90, 1);
    graphics.fillCircle(halfSize, halfSize, 6);

    // Leaves
    graphics.fillStyle(0x228B22, 1);
    graphics.fillEllipse(halfSize - 2, halfSize - 2, 4, 6);
    graphics.fillEllipse(halfSize + 2, halfSize - 2, 4, 6);
    graphics.fillEllipse(halfSize, halfSize + 2, 6, 4);

    // Sparkle effect
    graphics.fillStyle(0xFFFFFF, 0.8);
    graphics.fillCircle(halfSize - 2, halfSize - 3, 1.5);
    graphics.fillCircle(halfSize + 3, halfSize, 1);

    graphics.generateTexture('projectile-bomb', size, size);
    graphics.destroy();
  }

  private explode(): void {
    if (!this.active) return;

    // Minimal visual explosion effect for performance
    const explosionGraphics = this.weaponScene.add.graphics();
    explosionGraphics.fillStyle(0x90EE90, 0.4);
    explosionGraphics.fillCircle(this.x, this.y, this.explosionRadius);
    explosionGraphics.setDepth(10);

    this.weaponScene.tweens.add({
      targets: explosionGraphics,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        explosionGraphics.destroy();
      }
    });

    // Skip particles entirely for performance

    // Damage enemies in radius
    const enemyArray = this.enemies.getChildren() as Enemy[];
    enemyArray.forEach(enemy => {
      if (!enemy.active) return;

      const distance = Phaser.Math.Distance.Between(
        this.x, this.y,
        enemy.x, enemy.y
      );

      if (distance < this.explosionRadius) {
        enemy.takeDamage(this.damage);
      }
    });

    this.setActive(false);
    this.setVisible(false);
  }
}

export class CatnipBombWeapon extends BaseWeapon {
  private radiusMultiplier: number = 1;
  private attackDelay: number = GameConfig.WEAPON_DATA.CATNIP_BOMB.COOLDOWN;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    super(scene, player, enemies, CatnipBomb);
    this.startAutoAttack();

    this.projectiles.createMultiple({
      classType: CatnipBomb,
      key: 'projectile-bomb',
      repeat: 10 - 1,
      active: false,
      visible: false,
    });
  }

  protected startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: this.attackDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  protected attack(): void {
    // Throw bomb in a random nearby direction
    const angle = Math.random() * Math.PI * 2;
    const distance = 100 + Math.random() * 100;
    const targetX = this.player.x + Math.cos(angle) * distance;
    const targetY = this.player.y + Math.sin(angle) * distance;

    const bomb = this.projectiles.get(targetX, targetY) as CatnipBomb;
    if (bomb) {
      bomb.activate(
        targetX,
        targetY,
        GameConfig.WEAPON_DATA.CATNIP_BOMB.DAMAGE * this.damageMultiplier,
        GameConfig.WEAPON_DATA.CATNIP_BOMB.RADIUS * this.radiusMultiplier,
        GameConfig.WEAPON_DATA.CATNIP_BOMB.FUSE_TIME
      );
    }
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeSpeed(): void {
    this.attackDelay = Math.max(1000, this.attackDelay * GameConfig.WEAPON_DATA.CATNIP_BOMB.COOLDOWN_UPGRADE);
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
    this.startAutoAttack();
  }

  public upgradeRadius(amount: number): void {
    this.radiusMultiplier += amount;
  }
}
